import React, { useCallback, useMemo } from 'react';
import { ImageCarousel } from '~/components';

import Banner1 from '~/assets/img/home/banner-header-1.png';
import Banner2 from '~/assets/img/home/banner-header-2.png';
import Banner3 from '~/assets/img/home/banner-header-3.png';
import Banner4 from '~/assets/img/home/banner-header-4.png';

import BannerWeb1 from '~/assets/img/home/banner-web-header-1.png';
import BannerWeb2 from '~/assets/img/home/banner-web-header-2.png';
import BannerWeb3 from '~/assets/img/home/banner-web-header-3.png';
import BannerWeb4 from '~/assets/img/home/banner-web-header-4.png';
import colors from '~/assets/themes/colors';
import useAppNavigation from '~/hooks/useAppNavigation';
import { Dimensions, View } from 'react-native';
import { RouteScreens } from '~/navigation/routes';
import { MarisaAppUtils } from '~/utils/MarisaAppUtils';
import ContentLoader, { Rect } from 'react-content-loader/native';

interface Props {
  banners: Banner.Platform | undefined;
  isLoading?: boolean;
}

function isValidUrl(url: string): boolean {
  const urlPattern = /^(https?:\/\/)[\w.-]+\.[a-z]{2,}$/i;
  return urlPattern.test(url);
}

const BannersCarousel = ({ banners, isLoading = true }: Props) => {
  const { navigate } = useAppNavigation();

  const getRoute = useCallback((path: string) => {
    if (path && path !== '#') {
      const url = path.split('/').slice(2);

      let index = -1;
      const routes = Object.values(RouteScreens);
      for (let i = 0; i < url.length; i++) {
        const urlToFind = `/${url.slice(i).join('/')}`;

        index = routes.findIndex((route) => route === urlToFind);

        if (index > -1) {
          break;
        }
      }

      if (index > -1) {
        return {
          type: 'app',
          route: Object.keys(RouteScreens)[index] || '',
        };
      }

      return {
        type: 'browser',
        route: path,
      };
    }

    return null;
  }, []);

  const handleBannerPress = useCallback(
    (url: string) => {
      const screen = getRoute(url);

      if (screen) {
        //@ts-ignore
        navigate(screen.route);
      } else if (isValidUrl(url)) {
        MarisaAppUtils.openUrl(url);
      }
    },
    [getRoute, navigate]
  );

  const defaultBanners = useMemo(() => {
    return [
      {
        src: { mobile: Banner1, web: BannerWeb1 },
        chevronRigthColor: colors.primary[600],
        onPress: () => navigate('MarisaServicos_AboutMarisaCard'),
      },
      {
        src: { mobile: Banner2, web: BannerWeb2 },
        chevronRigthColor: colors.primary[600],
      },
      {
        src: { mobile: Banner3, web: BannerWeb3 },
        onPress: () => navigate('MarisaServicos_MarisaTakesCareDetails'),
      },
      {
        src: { mobile: Banner4, web: BannerWeb4 },
      },
    ];
  }, [navigate]);

  const carouselMobile = useMemo(() => {
    if (banners?.mobile?.items?.length) {
      return (
        banners.mobile?.items?.map((v) => ({
          src: { mobile: v.download, web: v.download },
          chevronRigthColor: colors.primary[600],
          onPress: () => handleBannerPress(v.url),
        })) || []
      );
    }

    return defaultBanners;
  }, [banners, handleBannerPress, defaultBanners]);

  const carouselWeb = useMemo(() => {
    if (banners?.web?.items?.length) {
      return (
        banners.web?.items?.map((v) => {
          return {
            src: { mobile: v.download, web: v.download },
            chevronRigthColor: colors.primary[600],
            onPress: () => handleBannerPress(v.url),
          };
        }) || []
      );
    }

    return defaultBanners;
  }, [banners, defaultBanners, handleBannerPress]);

  if (isLoading) {
    const width = Math.min(Dimensions.get('window').width, 1024);
    const height = width < 640 ? width : width / 3.44 + 26;
    return (
      <ContentLoader viewBox={`0 0 ${width} ${height}`}>
        <Rect width={width} height={height} />
      </ContentLoader>
    );
  }

  return (
    <>
      <View className="sm:hidden">
        <ImageCarousel images={carouselMobile} />
      </View>

      <View className="hidden sm:block">
        <ImageCarousel images={carouselWeb} />
      </View>
    </>
  );
};

export default BannersCarousel;
